import { PalmettoAutocomplete, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';

import { getEvaluationCore, ChecklistEvaluationKey } from 'types';
import type { EvaluationRowResult, EvaluationState, EvaluationView } from 'types';

import { FlagAlertBadge } from '../ChecklistInputFlagAlertBadge';
import type { EvaluationInputComponentProps } from '../evaluationClientRegistry';

interface SolarEdgeOptimizerEntered {
  model?: string | null;
  serial?: string | null;
}

interface SolarEdgeOptimizerDerived {
  inverterModel?: string;
}

const SOLAREDGE_MODEL_OPTIONS = [
  { label: 'U650', value: 'u650' },
  { label: 'U650B', value: 'u650b' },
  { label: 'S500B', value: 's500b' },
  { label: 'S440', value: 's440' },
  { label: 'Other', value: 'other' },
].toSorted((a, b) => a.label.localeCompare(b.label));

const inverterModelFromView = (view: EvaluationView | undefined): string | undefined => {
  const d = view?.rows[0]?.derived as unknown as SolarEdgeOptimizerDerived | undefined;
  return d?.inverterModel;
};

const enteredFromView = (view: EvaluationView | undefined): SolarEdgeOptimizerEntered => {
  const e = view?.rows[0]?.entered as unknown as SolarEdgeOptimizerEntered | undefined;
  return { model: e?.model ?? '', serial: e?.serial ?? '' };
};

const enteredSignature = (view: EvaluationView | undefined): string => JSON.stringify(enteredFromView(view));

const computeResult = (model: string, serial: string): EvaluationRowResult => {
  const core = getEvaluationCore(ChecklistEvaluationKey.solarEdgeOptimizerValidation);
  if (!core) return { verdict: 'incomplete' };
  const entered = { model, serial } as unknown as Record<string, number | null>;
  return core.computeRow({}, entered, {});
};

const buildState = (model: string, serial: string, result: EvaluationRowResult): EvaluationState => ({
  key: ChecklistEvaluationKey.solarEdgeOptimizerValidation,
  rows: [
    {
      entered: { model, serial } as unknown as EvaluationState['rows'][number]['entered'],
      verdict: result.verdict,
    },
  ],
  verdict: result.verdict,
});

export const SolarEdgeOptimizerEvaluationInput: React.FC<EvaluationInputComponentProps> = ({
  label,
  view,
  readOnly,
  onChange,
}) => {
  const [model, setModel] = useState<string>(() => enteredFromView(view).model ?? '');
  const [serial, setSerial] = useState<string>(() => enteredFromView(view).serial ?? '');
  const [seededSignature, setSeededSignature] = useState<string>(() => enteredSignature(view));

  // Re-seed local state when the persisted view changes (e.g. after a save round-trip).
  const currentSignature = enteredSignature(view);
  if (currentSignature !== seededSignature) {
    const seeded = enteredFromView(view);
    setModel(seeded.model ?? '');
    setSerial(seeded.serial ?? '');
    setSeededSignature(currentSignature);
  }

  const modelItems = useMemo(
    () =>
      SOLAREDGE_MODEL_OPTIONS.map((opt) => Object.assign({}, opt, { selected: opt.value === model.toLowerCase() })),
    [model],
  );

  const handleModelChange = useCallback(
    (details: { value: string[] }) => {
      if (!view) return;
      const nextModel = details.value[0] ?? '';
      setModel(nextModel);
      const result = computeResult(nextModel, serial);
      onChange(buildState(nextModel, serial, result));
    },
    [view, serial, onChange],
  );

  const handleSerialChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!view) return;
      const nextSerial = e.target.value;
      setSerial(nextSerial);
      const result = computeResult(model, nextSerial);
      onChange(buildState(model, nextSerial, result));
    },
    [view, model, onChange],
  );

  if (!view) return null;

  const inverterModel = inverterModelFromView(view);
  const result = computeResult(model, serial);
  const { verdict, qualificationType } = result;

  const badgeMessage =
    verdict === 'pass'
      ? qualificationType === 'nonPfeOnly'
        ? 'Qualifies — Non-PFE value only'
        : 'Qualifies'
      : verdict === 'fail'
        ? 'Does not qualify for domestic content'
        : 'Data unavailable';

  return (
    <div className="flex flex-col gap-8 py-4">
      <span className="body-bold-300 text-text-primary">{label}</span>

      {inverterModel !== undefined && (
        <div className="flex flex-col gap-2">
          <span className="body-200 text-text-secondary">Inverter Model</span>
          <span className="body-300 text-text-primary">{inverterModel}</span>
        </div>
      )}

      <PalmettoAutocomplete
        id="evaluation-solarEdgeOptimizer-model"
        label="Optimizer Model"
        items={modelItems}
        onValueChange={handleModelChange}
        disabled={readOnly}
      />

      <PalmettoTextInput
        id="evaluation-solarEdgeOptimizer-serial"
        label="Serial Number"
        value={serial}
        onChange={handleSerialChange}
        disabled={readOnly}
      />

      <FlagAlertBadge verdict={verdict} message={badgeMessage} />
    </div>
  );
};
